import { useEffect, useRef, useState, type JSX } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthError } from '@yuan-math/auth';
import { Button, Card } from '@yuan-math/ui';
import { useAuth } from '../state/AuthContext.tsx';
import { arrivalHref, linkCredential, resolveAuthLink } from '../state/authLink.ts';
import { AccountsUnavailable } from './RegisterPage.tsx';

/**
 * Activation.
 *
 * Serves two arrivals: someone who has just registered and is waiting for the
 * email, and someone returning by clicking the link in it. Which one is shown
 * depends on whether the URL carries a code.
 */
export function ActivatePage(): JSX.Element {
  const [params] = useSearchParams();
  const { resendActivation, configured, account, awaitingConsent, requestGuardianConsent } = useAuth();
  const navigate = useNavigate();

  const sentTo = params.get('sent');
  /*
   * Read from the URL the page was *opened* with, not the one in the address
   * bar now: the Supabase client removes the credential as soon as it has
   * consumed it, and it does so on a network round trip whose timing has
   * nothing to do with React's. Asking afterwards is a race, and losing it made
   * a working link report itself as broken.
   *
   * `linkCredential` also recognises an error in the hash, which the query-only
   * check missed — an expired link would then render "Check your email" instead
   * of the reason it failed.
   */
  const hasCredential = linkCredential(arrivalHref) !== 'none';

  const [state, setState] = useState<'idle' | 'working' | 'done' | 'failed'>(hasCredential ? 'working' : 'idle');
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  /*
   * Observe rather than exchange.
   *
   * The Supabase client is configured with `detectSessionInUrl`, so it consumes
   * the credential during its own initialisation — before this component
   * renders. Calling `exchangeCodeForSession` here would find the code already
   * spent and report a perfectly good link as invalid, which is exactly the bug
   * this replaced.
   */
  useEffect(() => {
    if (!hasCredential || !configured) return;
    let cancelled = false;

    void resolveAuthLink(arrivalHref).then((outcome) => {
      if (cancelled) return;
      if (outcome.status === 'signed-in') {
        setState('done');
        // Drop the credential from the address bar so a refresh or a shared
        // URL cannot replay it.
        window.history.replaceState({}, '', '/activate');
        return;
      }
      setError(outcome.status === 'failed' ? outcome.message : 'That link could not be used.');
      setState('failed');
    });

    return () => {
      cancelled = true;
    };
  }, [hasCredential, configured]);

  /*
   * The guardian's email, sent from the first moment there is a session to send
   * it with (§29).
   *
   * It used to be asked for during registration, where a project that confirms
   * email has not signed anybody in yet — so the request went out with the anon
   * key, the function refused it as it refuses any stranger asking us to mail
   * an address, and no guardian ever heard from us. Activation is the earliest
   * point the learner is authenticated, and it happens exactly once: the link
   * is single-use, and the effect above strips its credential from the address
   * bar, so a reload cannot reach this state and mail the guardian again.
   */
  const guardianAsked = useRef(false);

  useEffect(() => {
    if (state !== 'done' || !awaitingConsent || guardianAsked.current) return;
    guardianAsked.current = true;
    void requestGuardianConsent();
  }, [state, awaitingConsent, requestGuardianConsent]);

  if (!configured) return <AccountsUnavailable />;

  if (state === 'working') {
    return (
      <div className="page page--narrow page--centred">
        <h1 className="page__title">Activating your account…</h1>
        <p className="page__lead">One moment.</p>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">Your account is active.</h1>
        <p className="page__lead">
          Your progress will now follow you between devices. Everything you did before signing up has been kept.
        </p>
        {awaitingConsent ? (
          <Card>
            <p>
              One more thing: we have emailed{' '}
              <strong>{account?.guardianEmail ?? 'your parent or guardian'}</strong> to ask their permission. Keep
              learning in the meantime — the permission is what lets your progress sync between devices, and your
              account page can send it again if it does not arrive.
            </p>
          </Card>
        ) : null}
        <div className="button-row">
          <Button size="lg" onClick={() => navigate('/home')}>Start learning</Button>
          <Link to="/map" className="btn btn--secondary btn--lg">Explore the Math Map</Link>
        </div>
      </div>
    );
  }

  if (state === 'failed') {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">That link did not work</h1>
        <p className="page__lead">{error}</p>
        <Card>
          <p>
            Activation links last an hour, and each one can be used only once — including when an email provider
            opens it to scan it before you do. Requesting a new one is usually all that is needed.
          </p>
        </Card>
        <ResendForm onSent={() => setResent(true)} resent={resent} resendActivation={resendActivation} />
        {/* The failure may well be that this already worked. Somebody told
            "that account is already active" needs the door, not the sentence. */}
        <p className="page__note">
          Already activated? <Link to="/signin">Sign in</Link> instead.
        </p>
      </div>
    );
  }

  return (
    <div className="page page--narrow">
      <h1 className="page__title">Check your email</h1>
      <p className="page__lead">
        {sentTo ? (
          <>
            We have sent an activation link to <strong>{sentTo}</strong>. Follow it to finish setting up your
            account.
          </>
        ) : (
          'Follow the activation link we sent you to finish setting up your account.'
        )}
      </p>

      <Card>
        <p>
          Until you activate, the account exists but cannot be signed into. That gap is deliberate — it is what
          proves the address belongs to whoever is using it.
        </p>
      </Card>

      <ResendForm onSent={() => setResent(true)} resent={resent} resendActivation={resendActivation} initialEmail={sentTo ?? ''} />

      <p className="page__note">
        Nothing arrived? Check the spam folder — activation mail is often filtered on the first send.
      </p>
    </div>
  );
}

function ResendForm({
  resendActivation,
  onSent,
  resent,
  initialEmail = '',
}: {
  resendActivation(email: string): Promise<void>;
  onSent(): void;
  resent: boolean;
  initialEmail?: string;
}): JSX.Element {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const send = async (): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      await resendActivation(email);
      onSent();
      // Deliberately does not say whether the address is registered — that
      // would let a stranger test which addresses have accounts.
      setMessage('If that address has an account awaiting activation, a new link is on its way.');
    } catch (cause) {
      setMessage(cause instanceof AuthError ? cause.message : 'Could not send a new link just now.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="resend-form"
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      <label className="field">
        <span className="field__label">Send a new link</span>
        <div className="answer-form__row">
          <input
            className="field__input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
          <Button type="submit" variant="secondary" disabled={busy || email.trim() === '' || resent}>
            {busy ? 'Sending…' : resent ? 'Sent' : 'Resend'}
          </Button>
        </div>
      </label>
      {message ? <p className="field__hint" role="status">{message}</p> : null}
    </form>
  );
}
